<template>
    <NuxtImg
        v-if="!lazyLoad"
        v-bind="$attrs"
        :preload="preload"
        :fetchpriority="fetchPriority"
    />
    <template v-else>
        <NuxtImg
            v-if="intersecting"
            ref="mediaEl"
            v-bind="$attrs"
            :class="imageRendered ? undefined : loaderBackgroundClasses"
            :style="imageRendered ? undefined : reservedBoxStyle"
            :preload="preload"
            :fetchpriority="fetchPriority"
            @load="onImageRendered"
            @error="onImageRendered"
        />
        <img
            v-else
            ref="mediaEl"
            v-bind="loaderAttrs"
            :class="loaderClasses"
            :style="reservedBoxStyle"
            src="/images/loader.gif"
            alt="Image is loading..."
            data-not-lazy
        >
    </template>
</template>

<script setup lang="ts">
import { useEventListener, useIntersectionObserver } from '@vueuse/core'

// Both branches bind $attrs explicitly, so the loader doesn't inherit them too.
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    lazyLoad?: boolean,
    imgMinHeight?: string,
    preload?: boolean | { fetchPriority: 'high' | 'low' | 'auto' },
    // When true, unloads the image back to the placeholder once it scrolls far
    // outside the viewport, and reloads it once it scrolls back in to save memory.
    recede?: boolean,
    // Intrinsic dimensions from media.metaData, used only to reserve the box while loading.
    // Not `width`/`height`: NuxtImg turns those into resize modifiers and would then request
    // the full-size image.
    mediaWidth?: number | string | null,
    mediaHeight?: number | string | null,
}>(), {
    lazyLoad: false,
    imgMinHeight: 'min-h-none',
    preload: false,
    recede: false,
    mediaWidth: null,
    mediaHeight: null,
})

// NuxtImg's own `preload` prop only sets fetchpriority on the <link rel=preload> it injects
// into <head> — not on the rendered <img> itself. Lighthouse's LCP-request-discovery audit
// checks the actual element, so callers passing preload={fetchPriority:'high'} need it mirrored
// here explicitly (see docs/page-speed-audit/12-lcp-image-missing-fetchpriority-attribute.md).
const fetchPriority = computed(() => (
    typeof props.preload === 'object' && props.preload.fetchPriority ? props.preload.fetchPriority : undefined
))

const intersecting = ref(false)
const imageRendered = ref(false)
const mediaEl = ref<null | HTMLElement>(null)

// A wrapper or sibling element to keep the loader visible breaks the callers' layouts, so the
// image carries the loader as its own background until its first paint covers it. Plain string
// because Tailwind only picks up classes it can see as literals.
const loaderBackgroundClasses = 'bg-[url(\'/images/loader.gif\')] bg-center bg-contain bg-no-repeat'

const attrs = useAttrs()

// The loader mirrors the caller's sizing to sit in the same box the image will occupy. The rest
// of $attrs are NuxtImg props (format, sizes, …) and would be junk attributes on a plain <img>.
const loaderAttrs = computed(() => ({
    class: attrs.class,
    style: attrs.style,
    width: attrs.width as string | number | undefined,
    height: attrs.height as string | number | undefined,
}))

// Reserves the box responsively — the height follows the width the caller's layout gives the
// element. Dropped once the image is painted, which is a no-op since it then carries the same
// ratio intrinsically.
const reservedBoxStyle = computed(() => {
    const width = Number(props.mediaWidth)
    const height = Number(props.mediaHeight)
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return undefined
    return { aspectRatio: `${width} / ${height}` }
})

// Without dimensions there is no height to derive, so the old 4:3 / imgMinHeight box carries on.
const loaderClasses = computed(() => (
    reservedBoxStyle.value ? 'w-full object-contain' : computedClasses.value
))

// One frame after `load` the browser has painted. `error` clears it too, so a failing image
// can't keep spinning.
function onImageRendered() {
    requestAnimationFrame(() => {
        imageRendered.value = true
    })
}

// A receded image falls back to the loader, so the next load has to earn the swap again.
watch(intersecting, (value) => {
    if (!value) imageRendered.value = false
})

// mediaEl points at whichever branch is currently mounted (placeholder or the loaded
// image), so both observers below keep tracking the right node as it toggles.
useIntersectionObserver(mediaEl, ([entry]) => {
    if (entry?.isIntersecting) {
        intersecting.value = true
    }
})

if (props.recede) {
    useIntersectionObserver(mediaEl, ([entry]) => {
        if (entry && !entry.isIntersecting && intersecting.value) {
            intersecting.value = false
        }
    }, { rootMargin: '150% 150% 150% 150%' })
}

// Fallback for instant script-driven scroll jumps (e.g. back-navigation restore), which
// don't reliably redeliver an IntersectionObserver entry. Callers dispatch this after such
// a jump to force a direct viewport check.
if (props.lazyLoad) {
    useEventListener(window, 'a4g:recheck-lazy-media', () => {
        if (intersecting.value || !mediaEl.value) return
        const rect = mediaEl.value.getBoundingClientRect()
        const inViewport = rect.bottom > 0 && rect.right > 0
            && rect.top < window.innerHeight && rect.left < window.innerWidth
        if (inViewport) intersecting.value = true
    })
}

const computedClasses = computed(() => {
    if (props.imgMinHeight === 'min-h-none') {
        return 'aspect-[4/3] md:aspect-auto max-h-[300px] mx-auto'
    }
    return [props.imgMinHeight, 'aspect-[4/3] w-full'].filter(Boolean).join(' ')
})
</script>
